import {useMemo} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import ChekinProSettings from 'ChekinProSettings';
import {SHOWED_GUEST_GROUP_MODAL, IS_LEADER_GUEST_STORAGE_KEY} from 'utils/constants';
import {PATHS} from 'routes/constants';
import {SDK_MODE, SessionStorage} from '@chekin/core';
import {cn, DEVICE, useScreenResize} from '@chekin/guestapp-ui-kit';
import {appendLeaderParam} from 'utils/guestForm';
import {useIVOnProperty} from 'hooks/useIVOnProperty';
import {useIVStore} from '@chekin/shared';
import {useShareButtonStore} from 'components/ShareButton';
import {useGuestGroup, useModalOncePerSession, usePath} from 'hooks';
import {useLayoutMode} from 'hooks/useLayoutMode';
import {useSchema} from 'context/guest';
import {useReservation} from 'context/reservation';
import {useSummary} from 'context/summary';
import {useReservationSettingsModal} from 'components/ReservationSettingsModal';
import Page from '../../components/Page';
import GuestsPanel from 'components/GuestsPanel';
import {IncompleteGuestsModal} from './components/IncompleteGuestsModal';
import {IVOnPropertyAlertBoxes} from './components/IVOnPropertyAlertBoxes';
import {InformationText, HeaderGuestInformation, GuestsList} from './components';
import {PmsGuestPanelContainer, Wrapper} from './styled';

function GuestRegistrationView() {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {data: reservation, hasAvantio} = useReservation();
  const {isGuestRegistrationOnly, isInstantCheckInEnabled} = useSummary();
  const {guestGroup, isLoading} = useGuestGroup();
  const {getFullPath} = usePath();
  const {resetAllIVState} = useIVStore();
  const {isMatch: isMobile} = useScreenResize(DEVICE.laptop);
  const location = useLocation();
  const layoutMode = useLayoutMode();
  const openShareLinkModal = useShareButtonStore(state => state.openModal);

  const {
    identityVerificationDetails: {enabled: ivEnabled},
  } = useSchema();
  const {isBiometricMatchForAll} = useReservation();

  const {shouldOpenModal} = useModalOncePerSession({
    id: reservation?.id,
    storageKey: SHOWED_GUEST_GROUP_MODAL,
  });

  const {
    Modal: WhoIsComingModal,
    openModal,
    ageLimit,
  } = useReservationSettingsModal({
    openModalPerSession: shouldOpenModal,
    showFirstTime: isGuestRegistrationOnly,
  });

  const hasNoGuests = guestGroup?.number_of_fully_registered_guests === 0;
  const totalGuest = guestGroup?.known_number_of_guests || 5;
  const currentTotalGuest = guestGroup?.number_of_fully_registered_guests || 0;
  const {isIVOnPropertyUnavailable, isIVOnPropertyAvailable, isIVOnPropertyEnabled} =
    useIVOnProperty();

  const nextPage = useMemo(() => {
    if (isIVOnPropertyUnavailable) return getFullPath(PATHS.addPersonalDataForm);
    const goToIv = ivEnabled && (hasNoGuests || isBiometricMatchForAll);

    const goToForm = !ivEnabled || (ivEnabled && !isBiometricMatchForAll && !hasNoGuests);

    if (goToIv) {
      return isIVOnPropertyAvailable
        ? getFullPath(`${PATHS.verification.main}/${PATHS.verification.qrScan}`)
        : getFullPath(PATHS.verification.main);
    }
    if (goToForm) {
      return getFullPath(PATHS.addPersonalDataForm);
    }
    return isIVOnPropertyAvailable
      ? getFullPath(`${PATHS.verification.main}/${PATHS.verification.qrScan}`)
      : getFullPath(PATHS.verification.main);
  }, [
    ivEnabled,
    isIVOnPropertyUnavailable,
    isIVOnPropertyAvailable,
    getFullPath,
    isBiometricMatchForAll,
    hasNoGuests,
  ]);

  const onBackClick = () => {
    navigate(getFullPath(PATHS.home), {replace: true});
  };

  const handleRegisterClick = (isLeader?: boolean) => {
    resetAllIVState();
    navigate(appendLeaderParam(nextPage, isLeader));
  };

  const handleRegisterNewGuest = (isLeader?: boolean) => {
    SessionStorage.set(IS_LEADER_GUEST_STORAGE_KEY, isLeader);
    handleRegisterClick(isLeader);
  };

  const handleCompleteGuestData = () => {
    navigate(location.pathname, {replace: true, state: null});
  };

  return (
    <Page
      title={t('guest_summary')}
      isLoading={isLoading}
      onBackButtonClick={onBackClick}
      navBarTitle={
        isMobile || layoutMode === 'kiosko' ? t('guest_summary') : t('online_chekin')
      }
      className="guest-register-view"
      isSubHeaderVisible={
        ChekinProSettings.mode !== SDK_MODE.guestRegistrationOnly &&
        layoutMode === 'default'
      }
      isBackButtonDisabled={ChekinProSettings.mode === SDK_MODE.guestRegistrationOnly}
    >
      <Wrapper
        className={cn(
          'guest-register-view__content',
          'kiosko:w-full kiosko:mt-14 kiosko:items-start kiosko:gap-10 kiosko:max-w-full',
        )}
      >
        <HeaderGuestInformation
          currentTotalGuest={currentTotalGuest}
          totalGuest={totalGuest}
          onGuestCountClick={openModal}
        />
        {Boolean(ageLimit) && <InformationText ageLimit={ageLimit} />}
        {isIVOnPropertyEnabled && <IVOnPropertyAlertBoxes />}
        {hasAvantio || isInstantCheckInEnabled ? (
          <PmsGuestPanelContainer className="guest-register-view__guests-panel">
            <GuestsPanel handleRegisterNewGuest={handleRegisterNewGuest} />
          </PmsGuestPanelContainer>
        ) : (
          <GuestsList
            nextPage={nextPage}
            handleRegisterNewGuest={handleRegisterNewGuest}
          />
        )}
      </Wrapper>

      {WhoIsComingModal}

      <IncompleteGuestsModal
        onShareRegistrationLink={openShareLinkModal}
        onCompleteGuestData={handleCompleteGuestData}
      />
    </Page>
  );
}

export {GuestRegistrationView};
